import React from 'react'
import { useSelector } from 'hooks/use-redux'
import type { Track } from 'types/track'
import type { ManageNestingFormState } from '@disco/state-management/reducers/forms/manage-nesting'
import { getTrack } from '@disco/state-management/selectors/tracks'
import { addNewTracksAndShiftPosition } from '@disco/utils/nesting'
import ContextMenu from 'components/common/context-menu'
import TrackInfo from 'components/common/track-info'
import { Flex } from 'components/ds'
import Alert from 'components/ui/alert'
import ButtonContainer from 'components/ui/button-container'
import { HoverProvider } from 'components/ui/hover-provider'
import Stack from 'components/ui/stack'
import ManageNestingTrackSearch from 'pages/tracks/manage-nesting/track-search'
type Props = {
  formState: ManageNestingFormState
  nestedTracks: Track[]
  updateForm: (...args: Array<any>) => any
  tracksLoading: boolean
  error: Error | null | undefined
  hideButtons?: boolean
}

const ManageNestingHeaderActions = ({
  formState,
  nestedTracks,
  updateForm,
  tracksLoading,
  error,
  hideButtons,
}: Props): React.ReactNode => {
  const mainTrack = useSelector(state =>
    formState.mainTrackId
      ? getTrack(state, {
          id: formState.mainTrackId,
        })
      : null,
  )
  return (
    <Stack space="xsmall">
      <Flex gap="1" justify="between">
        {mainTrack ? (
          <HoverProvider>
            <TrackInfo location="explorer" track={mainTrack} />
          </HoverProvider>
        ) : null}
        {!hideButtons && (
          <ButtonContainer alignRight>
            <ContextMenu
              getMenuOffset={() => ['calc(-100% + 160px)', '36px']}
              appearance="action-menu"
              buttonProps={{
                appearance: 'subtle',
                caption: 'Add existing track',
                disabled: tracksLoading || formState.submitting,
                icon: null,
              }}
              buttonClassName={'NestingLabelDropdown'}
            >
              {() => {
                if (!formState.mainTrackId) return null
                return (
                  <ManageNestingTrackSearch
                    nestedTracks={nestedTracks}
                    mainTrackId={formState.mainTrackId}
                    onSelect={track => {
                      if (!formState.mainTrackId) return
                      updateForm(
                        addNewTracksAndShiftPosition({
                          tracks: nestedTracks,
                          newTracks: [
                            {
                              ...track,
                              nesting_parent_id: formState.mainTrackId,
                            },
                          ],
                        }),
                        {
                          type: 'ADD_NESTING_TRACK',
                        },
                      )
                    }}
                  />
                )
              }}
            </ContextMenu>
          </ButtonContainer>
        )}
      </Flex>

      {Boolean(error || formState.error) && (
        <Alert status="danger" icon="warning">
          {String(error || formState.error)}
        </Alert>
      )}
    </Stack>
  )
}

export default ManageNestingHeaderActions
